import React from 'react';
import { clone, isEmpty, map } from 'lodash';
import PropTypes from 'prop-types';
import { Bert } from 'meteor/themeteorchef:bert';

import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import FormatPaintIcon from '@mui/icons-material/FormatPaint';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import { Box, IconButton, Paper, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import { withTracker } from 'meteor/react-meteor-data';
import useTimeStore from '../ZustandProvider';

import BasicPopover from '../BasicPopover';
import GraphicsComponent from '../GraphicsComponent';
import MapLayerControlSideBySide from '../MapLayerControlSideBySide';
import StyleEditorComponent from '../StyleEditorComponent';
import Styles from '../../../api/Styles/Styles';
import TableComponent from '../TableComponent';
import { LAYER_SUBTYPES } from '../../../api/Layers/Layers';

import * as mapOps from '../../../api/Maps/map-operations';
import { renderSwitch } from '../MaterialMUIComponent';

const MapLayerOrder = props => {
  const { MapProps, style } = props;
  const { ViewHtml } = MapProps.map.selectedButtons;
  const { showDownloadLayer } = MapProps.map.settingsAdvancedMap.buttonSettings;

  const isSideBySide = useTimeStore(state => state.isSideBySide);
  const setIsSideBySide = useTimeStore(state => state.setIsSideBySide);
  const layers = useTimeStore(state => state.layers);
  const setLayers = useTimeStore(state => state.setLayers);

  const [elementToRenderAux, setElementToRenderAux] = React.useState('');
  const [componentToRender, setComponentToRender] = React.useState('');
  const [elementToRender, setElementToRender] = React.useState('');
  const [layerIndexStyle, setLayerIndexStyle] = React.useState(0);

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: '#2A3F90',
      color: theme.palette.common.white
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14
    }
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover
    },
    '&:last-child td, &:last-child th': {
      border: 0
    }
  }));

  const StyledTable = styled(Table)(() => ({
    border: `1px solid #2A3F90`
  }));

  const addStyle = prop => {
    const A = layers;
    const index = layers.findIndex(x => x._id === prop._id);
    A[index] = prop;
    setLayers([...A]);
  };
  const handleChange = value => {
    const index = layers.findIndex(x => x._id === value);
    setLayerIndexStyle(index);
    return index;
  };

  const moveItem = (array, from, to) => {
    const arrayToMutate = clone(array);
    // remove `from` item and store it
    const f = arrayToMutate.splice(from, 1)[0];
    // insert stored item into position `to`
    arrayToMutate.splice(to, 0, f);
    return arrayToMutate;
  };

  const moveLayer = (where, layerId) => {
    const currentLayerOrder = [...new Set(map(layers, item => item._id))];
    const layerIndex = currentLayerOrder.indexOf(layerId);
    let movedArray;
    if (where === 'down') {
      if (layerIndex === currentLayerOrder.length - 1) {
        return false;
      }
      movedArray = moveItem(layers, layerIndex, layerIndex + 1);
    } else {
      if (layerIndex === 0) {
        return false;
      }
      if (where === 'top') {
        movedArray = moveItem(layers, layerIndex, 0);
      } else {
        movedArray = moveItem(layers, layerIndex, layerIndex - 1);
      }
    }
    setLayers([...movedArray]);
    return true;
  };

  const handleDownload = async layerProp => {
    if (layerProp.layerSubType === LAYER_SUBTYPES.GEOJSON) {
      if (!layerProp.url) {
        console.error('No se proporcionó una URL para la descarga. Saliendo del proceso.');
        return;
      }

      const filename = `${layerProp.name.trim()}.json`;
      const response = await fetch(layerProp.url);

      if (!response.ok) {
        console.error('Error al obtener el archivo. Código:', response.status);
        return;
      }

      const blob = await response.blob();
      const downloadLink = window.URL.createObjectURL(blob);

      const anchorElement = document.createElement('a');
      anchorElement.href = downloadLink;
      anchorElement.setAttribute('download', filename);
      anchorElement.click();

      setTimeout(() => window.URL.revokeObjectURL(downloadLink), 1000);
    } else {
      Bert.alert('This layer cannot be downloaded', 'warning');
    }
  };

  const renderComponent = async prop => {
    const { component, index } = prop;
    setElementToRender(component);
    switch (component) {
      case 'Style':
        setComponentToRender(
          <StyleEditorComponent
            addStyle={addStyle}
            deployEnv={MapProps.deployEnv}
            history={MapProps.history}
            layer={layers[index]}
            match={MapProps.match}
            style={style}
          />
        );
        break;
      case 'Graphics':
        setComponentToRender(<GraphicsComponent layer={layers[index]} {...props} />);
        break;
      case 'NewTable':
        setComponentToRender(
          <TableComponent rowsInitial={5} isMapProp layer={layers[index]} ViewHtml={ViewHtml} />
        );
        break;
      default:
        setComponentToRender('');
        break;
    }
    setElementToRenderAux(prop);
  };
  React.useEffect(() => {
    renderComponent(elementToRenderAux);
  }, [props]);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, padding: 2, height: '100%' }}>
      <TableContainer component={Paper} sx={{ maxHeight: 530, overflow: 'hidden' }}>
        <StyledTable aria-label="customized table" size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <StyledTableCell align="left">CAPAS</StyledTableCell>
              <StyledTableCell align="right">
                <br />
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {map(layers, ({ _id, name }) => {
              return (
                <StyledTableRow key={_id}>
                  <StyledTableCell component="th" scope="row">
                    {name}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    <Stack
                      color="inherit"
                      size="small"
                      direction="row"
                      justifyContent="flex-end"
                      alignItems="center"
                      spacing={0}
                    >
                      <BasicPopover id={_id} />
                      <IconButton
                        onClick={() => {
                          const idx = handleChange(_id);
                          renderComponent({ component: 'Graphics', index: idx });
                        }}
                        sx={{
                          color:
                            _id === layers[layerIndexStyle]._id &&
                            elementToRender === 'Graphics' &&
                            '#2A3F90'
                        }}
                      >
                        <LeaderboardIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          const idx = handleChange(_id);
                          renderComponent({ component: 'Style', index: idx });
                        }}
                        sx={{
                          color:
                            _id === layers[layerIndexStyle]._id &&
                            elementToRender === 'Style' &&
                            '#2A3F90'
                        }}
                      >
                        <FormatPaintIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          const idx = handleChange(_id);
                          renderComponent({ component: 'NewTable', index: idx });
                        }}
                        sx={{
                          color:
                            _id === layers[layerIndexStyle]._id &&
                            elementToRender === 'NewTable' &&
                            '#2A3F90'
                        }}
                      >
                        <TextSnippetIcon />
                      </IconButton>
                      {showDownloadLayer === true && (
                        <IconButton
                          onClick={() => {
                            const layerSelected = layers.find(objeto => objeto._id === _id);
                            handleDownload(layerSelected);
                          }}
                        >
                          <DownloadIcon />
                        </IconButton>
                      )}
                      <IconButton
                        edge="end"
                        onClick={() => moveLayer('top', _id)}
                        aria-label="comments"
                      >
                        <KeyboardDoubleArrowUpIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        onClick={() => moveLayer('up', _id)}
                        aria-label="comments"
                      >
                        <KeyboardArrowUpIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        onClick={() => moveLayer('down', _id)}
                        aria-label="comments"
                      >
                        <KeyboardArrowDownIcon />
                      </IconButton>
                    </Stack>
                  </StyledTableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </StyledTable>
      </TableContainer>
      <br />

      {!isEmpty(componentToRender) && (
        <Box
          sx={{
            position: 'relative',
            width: 'auto',
            height: 'auto'
          }}
        >
          <IconButton
            sx={{
              position: 'absolute',
              color: '#2A3F90',
              top: '0px',
              right: '0px'
            }}
            onClick={() => setComponentToRender('')}
          >
            <CloseIcon />
          </IconButton>
          {componentToRender}
        </Box>
      )}
      {mapOps.showSideBySide(MapProps.map) === true && (
        <Box
          sx={{
            position: 'relative',
            width: 'auto',
            height: 'auto'
          }}
        >
          {' '}
          {renderSwitch({
            key: 'SideBySideMap',
            checked: isSideBySide,
            onChange: () => setIsSideBySide(!isSideBySide),
            labelCheck: 'Side by side map',
            labelUncheck: 'Side by side map'
          })}
          {isSideBySide === true && <MapLayerControlSideBySide MapProps={MapProps} />}
        </Box>
      )}
    </Box>
  );
};

MapLayerOrder.defaultProps = {
  MapProps: {},
  style: {}
};

MapLayerOrder.propTypes = {
  MapProps: PropTypes.object,
  style: PropTypes.object
};

export default withTracker(({ MapProps }) => {
  const styleId = MapProps.match.params.style_id;

  return {
    style: Styles.findOne(styleId)
  };
})(MapLayerOrder);
